import React, { useEffect } from "react";

import {
	Button,
	Checkbox,
	InfoMessage,
	PasswordField,
	Picto,
	Spinner,
	Input,
	Title,
} from "@amphore-dev/amphore-lib";
import { Form, Formik, useFormikContext } from "formik";
import i18next, { t } from "i18next";
import { Trans } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";

import { useMutation } from "@tanstack/react-query";

import AuthService from "@/services/AuthService";
import UserService from "@/services/UserService";

import { useAuth } from "@/hooks/useAuth";

import { EditableAvatar } from "@/components/molecules";

import { getApiErrormessage } from "@/utils/UAPIErrors";
import {
	emailSchema,
	fieldError,
	passwordSchema,
	touchAndSet,
} from "@/utils/UForms";
import { rememberPlanIntent } from "@/utils/UPlanIntent";
import { ucFirst } from "@/utils/UStrings";

import { TLegalPage, legalUrl } from "@/constants/CLegal";
import { USERNAME_REGEX } from "@/constants/CRegex";

import { TRegisterFormValues } from "@/types";
import { TUsernameCheck } from "@/types";

const initialValues: TRegisterFormValues = {
	username: "",
	firstName: "",
	lastName: "",
	email: "",
	password: "",
	confirmPassword: "",
	fromSSO: false,
	avatar: undefined,
	authType: "internal",
	acceptTerms: false,
};

const validationSchema = () => {
	return Yup.object().shape({
		username: Yup.string()
			.required(t("forms.username.required"))
			.min(3, t("forms.min", { value: 3 }))
			.max(30, t("forms.max", { value: 30 }))
			.matches(
				USERNAME_REGEX,
				t("forms.username.invalid", {
					interpolation: { escapeValue: false },
				})
			),
		email: emailSchema().required(t("forms.email.required")),
		firstName: Yup.string()
			.required(t("forms.required"))
			.max(100, t("forms.max", { value: 100 })),
		lastName: Yup.string()
			.required(t("forms.required"))
			.max(100, t("forms.max", { value: 100 })),
		password: Yup.string().when("fromSSO", {
			is: (value: boolean) => !!value,
			then: () => Yup.string().notRequired(),
			otherwise: () => passwordSchema(),
		}),

		acceptTerms: Yup.boolean().oneOf([true], t("register.terms.required")),
		confirmPassword: Yup.string().when("fromSSO", {
			is: (value: boolean) => !!value,
			then: () => Yup.string().notRequired(),
			otherwise: () =>
				Yup.string()
					.required(t("forms.password.required"))
					.oneOf([Yup.ref("password")], t("forms.password.match")),
		}),
	});
};

/** A link to a legal text on the site, or the plain words while it has no URL. */
const LegalLink: React.FC<React.PropsWithChildren<{ page: TLegalPage }>> = ({
	page,
	children,
}) => {
	const url = legalUrl(page, i18next.language);
	if (!url) return <span>{children}</span>;

	return (
		<a
			href={url}
			target="_blank"
			rel="noopener noreferrer"
			className="text-primary underline"
		>
			{children}
		</a>
	);
};

/** The consent the API requires: no account without the terms and the privacy policy. */
const TermsCheckbox: React.FC<{
	checked: boolean;
	error?: string;
	onChange: (checked: boolean) => void;
}> = ({ checked, error, onChange }) => (
	<div className="col gap-1">
		<div className="flex items-start gap-2">
			<Checkbox
				id="acceptTerms"
				name="acceptTerms"
				checked={checked}
				hideError
				aria-describedby={error ? "acceptTerms-error" : undefined}
				onChange={onChange}
			/>
			<label htmlFor="acceptTerms" className="text-sm text-neutral-600">
				<Trans
					i18nKey="register.terms.accept"
					components={{
						terms: <LegalLink page="terms" />,
						privacy: <LegalLink page="privacy" />,
					}}
				/>
			</label>
		</div>
		{error && (
			<p id="acceptTerms-error" className="text-sm text-red-600">
				{error}
			</p>
		)}
	</div>
);

export const Register: React.FC = () => {
	const [errors, setErrors] = React.useState("");
	const { login } = useAuth();

	// get location state
	const location = useLocation();
	const state = (location.state || {}) as {
		avatar?: Blob;
		provider?: string;
		authType?: string;
		name?: string;
		fromSSO?: boolean;
		email?: string;
		username?: string;
	};
	const avatarUrlRef = React.useRef<string | undefined>(
		state.avatar ? URL.createObjectURL(state.avatar) : undefined
	);

	const navigate = useNavigate();

	// `?plan=` from the site's pricing page: offered once the account is
	// set up, not now — the trial covers everything meanwhile.
	const [searchParams] = useSearchParams();
	React.useEffect(() => {
		rememberPlanIntent(searchParams.get("plan"));
	}, [searchParams]);
	// From an invitation mail: the address it was sent to, and its token,
	// which proves that address - the access follows the sign-up at once.
	const invitation = searchParams.get("invitation") ?? undefined;
	const invitedEmail = searchParams.get("email") ?? undefined;

	const handleSubmit = (values: TRegisterFormValues) => {
		setErrors("");

		return AuthService.register({ ...values, invitation }).then(
			(token) => {
				return UserService.fetchMe(token.access_token).then((user) => {
					login(token, user);
					navigate("/");
					return user;
				});
			},
			(err) => {
				setErrors(getApiErrormessage(err));
			}
		);
	};

	useEffect(() => {
		return () => {
			if (avatarUrlRef.current) {
				URL.revokeObjectURL(avatarUrlRef.current);
			}
		};
	}, []);

	return (
		<div className="w-full">
			<Title size="h3" className="text-center">
				{[
					t("register.title"),
					state.provider && state.authType !== "internal"
						? t("auth.errors.sso.with_provider", {
								provider: ucFirst(state.provider),
							})
						: "",
				].join(" ")}
			</Title>
			<Formik
				initialValues={{
					...initialValues,
					username: state.name || "",
					email: invitedEmail ?? "",
					...state,
				}}
				onSubmit={handleSubmit}
				validationSchema={validationSchema}
				validateOnBlur={true}
			>
				{(formik) => {
					const { values, isSubmitting, setFieldValue } = formik;
					return (
						<Form className="col mt-4 gap-4">
							<EditableAvatar
								wrapperClassName="w-32 h-32 mx-auto"
								url={avatarUrlRef.current}
								uploadLabel={t("user.avatarUpload")}
								onChange={(file) => {
									if (avatarUrlRef.current) {
										URL.revokeObjectURL(
											avatarUrlRef.current
										);
									}
									avatarUrlRef.current = file
										? URL.createObjectURL(file)
										: undefined;
									setFieldValue("avatar", file);
								}}
							/>
							<UserNameField />
							<Input
								name="firstName"
								error={fieldError(formik, "firstName")}
								autoComplete="given-name"
								label={t("user.firstName")}
								value={values.firstName}
								onChange={(value) =>
									touchAndSet(formik, "firstName", value)
								}
							/>
							<Input
								name="lastName"
								error={fieldError(formik, "lastName")}
								autoComplete="family-name"
								label={t("user.lastName")}
								value={values.lastName}
								onChange={(value) =>
									touchAndSet(formik, "lastName", value)
								}
							/>
							<Input
								name="email"
								error={fieldError(formik, "email")}
								type="email"
								label={t("forms.email.email")}
								autoComplete="email"
								value={values.email}
								onChange={(value) =>
									touchAndSet(formik, "email", value)
								}
								readOnly={values.fromSSO}
								disabled={values.fromSSO}
							/>
							{!values.fromSSO && (
								<>
									<PasswordField
										name="password"
										error={fieldError(formik, "password")}
										autoComplete="new-password"
										label={t("forms.password.password")}
										value={values.password}
										onChange={(value) =>
											touchAndSet(
												formik,
												"password",
												value
											)
										}
									/>
									<PasswordField
										name="confirmPassword"
										error={fieldError(
											formik,
											"confirmPassword"
										)}
										autoComplete="new-password-confirmation"
										label={t("forms.password.confirm")}
										value={values.confirmPassword}
										onChange={(value) =>
											touchAndSet(
												formik,
												"confirmPassword",
												value
											)
										}
									/>
								</>
							)}
							<TermsCheckbox
								checked={values.acceptTerms}
								error={fieldError(formik, "acceptTerms")}
								onChange={(checked) =>
									touchAndSet(formik, "acceptTerms", checked)
								}
							/>
							{!!errors && (
								<InfoMessage color="danger">
									{errors}
								</InfoMessage>
							)}
							<Button type="submit" isLoading={isSubmitting}>
								{t("register.register")}
							</Button>
						</Form>
					);
				}}
			</Formik>
			<hr className="my-4" />
			<div className="flex justify-center text-neutral-500 hover:*:text-black">
				<button
					onClick={() => {
						navigate("/login");
					}}
				>
					{t("auth.signIn")}
				</button>
			</div>
		</div>
	);
};

/**
 * The username, checked against the API as it is typed. Two sources of
 * error, one place to show them: the schema's (format, length) first,
 * then the API's ("already taken"). The API is only asked about a name
 * the schema accepts.
 */
const UserNameField = () => {
	const formik = useFormikContext<TRegisterFormValues>();
	const { values, errors } = formik;
	const timeout = React.useRef<number | null>(null);

	const handleFetch = (value: string) => {
		if (timeout.current) clearTimeout(timeout.current);

		if (!value || value.match(USERNAME_REGEX) === null) {
			return Promise.resolve(null);
		}

		return new Promise<TUsernameCheck | null>((resolve, reject) => {
			timeout.current = window.setTimeout(() => {
				AuthService.checkUsername(value).then(resolve, reject);
			}, 300);
		});
	};

	const checkUsernameMutation = useMutation<
		TUsernameCheck | null,
		Error,
		string
	>({
		mutationFn: (username: string) => handleFetch(username),
	});

	const error =
		fieldError(formik, "username") ??
		(checkUsernameMutation.error
			? getApiErrormessage(checkUsernameMutation.error)
			: undefined);

	return (
		<Input
			name="username"
			label={t("user.username")}
			value={values.username}
			onChange={(value) => {
				// Format errors show as the name is typed, like every field's.
				touchAndSet(formik, "username", value);
				// mutate, not mutateAsync: the outcome is read from the
				// mutation state below, and a rejected mutateAsync promise
				// nobody awaits surfaces as an unhandled rejection.
				checkUsernameMutation.mutate(value);
			}}
			autoComplete="username"
			error={error}
			after={
				<>
					{checkUsernameMutation.isPending && (
						<Spinner className="shrink-0" />
					)}
					{!checkUsernameMutation.isPending &&
						!errors.username &&
						checkUsernameMutation.data && (
							<Picto
								icon="checkCircle"
								className="h-6 w-6 shrink-0 text-green-500!"
							/>
						)}
					{!checkUsernameMutation.isPending && error && (
						<Picto
							icon="crossCircle"
							className="h-6 w-6 shrink-0 text-red-500!"
						/>
					)}
				</>
			}
		/>
	);
};
